import { useState } from "preact/hooks";
import { useAuth } from "./db.ts";
import { useIsMobile } from "./uiComponents.tsx";

export const ROUTES = {
  HOME: "/",
  APP: "/app",
  TERMS: "/terms",
  PRIVACY: "/privacy",
  SUBS: "/subs",
  DOCS: "/docs",
  SETTINGS: "/settings",
  PRICING: "/pricing",
} as const;

const CODE_EXAMPLES = {
  skill: `# Install via Tank (Claude Code, Cursor, OpenCode)
tank install @uriva/find-scene

# Or point your agent to our llms.txt
# https://find-scene.com/llms.txt`,

  curl: `# 1. Resolve movie to a video source (returns async operation ID)
curl -X POST https://api.find-scene.com/api/get_best_video_source \\
  -H "Content-Type: application/json" \\
  -d '{
    "_token": "fs_live_YOUR_API_TOKEN",
    "title": "The Matrix"
  }'

# 2. Poll until completed to retrieve videoSourceHash
curl https://api.find-scene.com/api/operation/op_video_123
# returns { "status": "completed", "result": { "videoSourceHashes": ["video-3b7da43b43"] } }

# 3. Cut precision clip by time using the resolved videoSourceHash
curl -X POST https://api.find-scene.com/api/download_by_time \\
  -H "Content-Type: application/json" \\
  -d '{
    "_token": "fs_live_YOUR_API_TOKEN",
    "videoSourceHash": "video-3b7da43b43",
    "startTime": "00:43:10",
    "endTime": "00:43:45"
  }'`,

  typescript: `// Call find-scene from TypeScript or Node.js
const token = process.env.FIND_SCENE_TOKEN;

// 1. Resolve movie to a video source
const sourceRes = await fetch("https://api.find-scene.com/api/get_best_video_source", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    _token: token,
    title: "The Matrix",
  }),
});
const { result: sourceOp } = await sourceRes.json();

// 2. Poll operation to get the videoSourceHash
const statusRes = await fetch(
  \`https://api.find-scene.com/api/operation/\${sourceOp.operationId}\`,
);
const { result: sourceData } = await statusRes.json();
const videoSourceHash = sourceData.videoSourceHashes[0];

// 3. Cut precision clip using the resolved videoSourceHash
const cutRes = await fetch("https://api.find-scene.com/api/download_by_time", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    _token: token,
    videoSourceHash,
    startTime: "00:43:10",
    endTime: "00:43:45",
  }),
});
const { result: cutOp } = await cutRes.json();
console.log("Download operation ID:", cutOp.operationId);`,
};

const FAQ_ITEMS = [
  {
    q: "How does visual scene search work when there is no dialogue?",
    a: "We analyze visual actions, settings, camera movements, and lighting across our catalog. When you describe what happens visually, the search pinpoints the matching timestamp even when nobody speaks.",
  },
  {
    q: "What is 9:16 smart cropping?",
    a: "Standard 9:16 crops simply slice out the middle of widescreen footage, frequently cutting off characters standing on the sides. Smart cropping tracks character motion and eye direction to keep the primary subject centered with natural, smooth pans.",
  },
  {
    q: "Can I equip my AI agent (Claude Code, Cursor, custom bots) with find-scene?",
    a: "Yes. find-scene is designed natively for AI agents. You can point your agent to our llms.txt (https://find-scene.com/llms.txt) or install our skill via Tank (tank install @uriva/find-scene). Agents can identify movies from partial quotes, find timestamps, download clips, extract uncompressed stills, and analyze cinema footage autonomously.",
  },
  {
    q: "How fast do clip cuts and frame grabs run?",
    a: "Quote searches and subtitle lookups return in under a second. Video clip cuts and frame extractions process in the cloud in just a few seconds without needing large downloads on your machine.",
  },
  {
    q: "Can I download full movies or TV episodes?",
    a: "No. find-scene is strictly a scene discovery and research tool. Video clip cuts are capped at five minutes maximum to keep usage firmly aligned with fair use for commentary, criticism, education, parody, and research.",
  },
  {
    q: "Where do I find my API token?",
    a: "Sign in with your email magic code, navigate to Settings, and click Generate new token. You can generate multiple tokens with custom labels, monitor monthly credit consumption, or revoke keys at any time.",
  },
];

export const Landing = () => {
  const isMobile = useIsMobile();
  const auth = useAuth();
  const isLoggedIn = !!auth.user;

  const [copiedCodeKey, setCopiedCodeKey] = useState<string | null>(null);
  const [devTab, setDevTab] = useState<keyof typeof CODE_EXAMPLES>("skill");
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCodeKey(key);
    setTimeout(() => {
      setCopiedCodeKey(null);
    }, 2000);
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
        minHeight: "100%",
        color: "#ccc",
        backgroundColor: "#1a1a1a",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      {/* Background Ambient Glow */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: 1200,
          height: 600,
          background:
            "radial-gradient(circle at 50% 10%, rgba(110, 53, 91, 0.22) 0%, rgba(26, 26, 26, 0) 70%)",
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Hero Section */}
      <section
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: isMobile ? "36px 16px 28px" : "64px 24px 44px",
          maxWidth: 960,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Category Pill */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "6px 16px",
            borderRadius: 999,
            backgroundColor: "rgba(224, 192, 216, 0.08)",
            border: "1px solid rgba(224, 192, 216, 0.22)",
            color: "#e0c0d8",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: 0.5,
            marginBottom: 24,
          }}
        >
          <span>AI-Powered Cinema Retrieval & Clip Extraction</span>
        </div>

        {/* Main Headline */}
        <h1
          style={{
            margin: "0 0 18px",
            fontSize: isMobile ? 32 : 54,
            fontWeight: 800,
            lineHeight: 1.15,
            letterSpacing: "-0.03em",
            color: "#ffffff",
          }}
        >
          Find any movie or TV scene in seconds
        </h1>

        {/* Subtitle */}
        <p
          style={{
            margin: "0 auto 32px",
            maxWidth: 680,
            fontSize: isMobile ? 16 : 19,
            lineHeight: 1.55,
            color: "#b0b0b0",
          }}
        >
          Search by memorable dialogue, visual actions, or exact time. Free to
          try without a credit card.
        </p>

        {/* Hero CTAs */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 14,
            width: isMobile ? "100%" : "auto",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <a
            href={ROUTES.APP}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              height: 48,
              minHeight: 48,
              width: isMobile ? "100%" : "auto",
              padding: "0 32px",
              borderRadius: 10,
              backgroundColor: "#4a223e",
              color: "#f5d5ee",
              fontSize: 16,
              fontWeight: 600,
              textDecoration: "none",
              border: "1px solid rgba(224, 192, 216, 0.35)",
              boxSizing: "border-box",
              cursor: "pointer",
              transition: "transform 0.15s ease, background-color 0.2s ease",
            }}
          >
            <img
              src="/favicon.svg"
              alt=""
              width={20}
              height={20}
              style={{ display: "block" }}
            />
            <span>
              {isLoggedIn ? "Open Web App" : "Start Finding Scenes Free"}
            </span>
          </a>

          <a
            href={ROUTES.DOCS}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 48,
              minHeight: 48,
              width: isMobile ? "100%" : "auto",
              padding: "0 26px",
              borderRadius: 10,
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              color: "#e0c0d8",
              fontSize: 15,
              fontWeight: 500,
              textDecoration: "none",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
          >
            API & Agent Skill
          </a>
        </div>
      </section>

      {/* How It Works: 3 Linear Steps */}
      <section
        style={{
          width: "100%",
          maxWidth: 960,
          padding: isMobile ? "28px 16px" : "44px 24px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: isMobile ? 26 : 36,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            How find-scene works
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: "#999" }}>
            From memory to usable media in three steps without downloading giant
            files.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: "rgba(224, 192, 216, 0.15)",
                color: "#e0c0d8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 16,
              }}
            >
              1
            </div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 18,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Quote, describe, or timestamp
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Type dialogue you remember, a visual scene action like a rooftop
              chase, or an exact time code. The search handles typos, phonetic
              variants, and partial quotes.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: "rgba(224, 192, 216, 0.15)",
                color: "#e0c0d8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 16,
              }}
            >
              2
            </div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 18,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Pinpoint the exact moment
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Our search scans subtitle transcripts and visual scene records to
              locate the moment and isolate the start and end points down to the
              frame.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                backgroundColor: "rgba(224, 192, 216, 0.15)",
                color: "#e0c0d8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 16,
              }}
            >
              3
            </div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 18,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Export ready-to-share media
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Fast cloud processing trims the MP4 segment, renders 9:16 smart
              crops, or extracts full-resolution still frames ready for
              immediate download or API integration.
            </p>
          </div>
        </div>
      </section>

      {/* Feature Grid: 6 Capabilities */}
      <section
        style={{
          width: "100%",
          maxWidth: 960,
          padding: isMobile ? "24px 16px" : "40px 24px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: isMobile ? 26 : 36,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Built for creators, researchers, and AI agents
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: "#999" }}>
            Six dedicated tools in one unified engine.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Dialogue & Quote Search
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Match lines of dialogue with millisecond precision across
              thousands of titles. Handles paraphrasing, partial recollections,
              and character banter with fuzzy text matching.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Visual Scene Search
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Search by what happens on screen even when nobody speaks. Find
              stunts, camera angles, specific lighting, and action sequences
              using natural language descriptions.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              9:16 Smart Crop
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Turn widescreen movies into natural vertical video for TikTok,
              Shorts, and Reels. Smart crop tracks character motion to keep the
              focal point centered smoothly across shots.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Lossless HD Frame Extraction
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Pull pristine still frames at full resolution. Perfect for video
              essay thumbnails, cinematography studies, and film analysis
              without browser compression.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              Side-by-Side & Clip Stitching
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Combine multiple scenes into supercuts or render synchronized
              side-by-side split screens to compare homages, shot compositions,
              or multiple camera angles.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: 12,
              padding: 24,
            }}
          >
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: 19,
                fontWeight: 700,
                color: "#ffffff",
              }}
            >
              AI Agent Skill & OpenAPI
            </h3>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                lineHeight: 1.6,
                color: "#a0a0a0",
              }}
            >
              Add find-scene as a skill to Claude Code, Cursor, or your
              autonomous scripts. Every capability is available as a typed REST
              endpoint documented with OpenAPI 3.0.
            </p>
          </div>
        </div>
      </section>

      {/* Developer Quickstart & Agent Skill */}
      <section
        id="quickstart"
        style={{
          width: "100%",
          maxWidth: 960,
          padding: isMobile ? "24px 16px" : "40px 24px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: isMobile ? 26 : 36,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Developer Quickstart & Agent Skill
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: "#999" }}>
            Equip your autonomous AI workflows with precision scene retrieval.
          </p>
        </div>

        <div
          style={{
            backgroundColor: "#201c23",
            border: "1px solid rgba(224, 192, 216, 0.2)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          {/* Code Tabs Header */}
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              overflowX: "auto",
            }}
          >
            {(
              [
                ["skill", "Claude / Cursor Skill"],
                ["curl", "cURL REST API"],
                ["typescript", "TypeScript"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setDevTab(key)}
                style={{
                  padding: "14px 20px",
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom: devTab === key
                    ? "2px solid #e0c0d8"
                    : "2px solid transparent",
                  color: devTab === key ? "#ffffff" : "#888",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Code Viewer Body */}
          <div style={{ position: "relative", padding: 20 }}>
            <button
              type="button"
              onClick={() => copyToClipboard(CODE_EXAMPLES[devTab], devTab)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                height: 36,
                minHeight: 36,
                padding: "0 14px",
                borderRadius: 8,
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                color: "#ccc",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {copiedCodeKey === devTab ? "Copied!" : "Copy Snippet"}
            </button>

            <pre
              style={{
                margin: 0,
                fontFamily: "monospace",
                fontSize: 13,
                lineHeight: 1.6,
                color: "#e0c0d8",
                overflowX: "auto",
                paddingRight: 80,
              }}
            >
              <code>{CODE_EXAMPLES[devTab]}</code>
            </pre>
          </div>
        </div>
      </section>

      {/* Pricing & Free Quota */}
      <section
        style={{
          width: "100%",
          maxWidth: 960,
          padding: isMobile ? "24px 16px" : "40px 24px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: isMobile ? 26 : 36,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Pricing
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: "#999" }}>
            Free tier every month. Pay only when you process heavy video.
          </p>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: 20,
          }}
        >
          {/* Free Tier */}
          <div
            style={{
              backgroundColor: "#201c23",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 14,
              padding: 28,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#aaa",
                  letterSpacing: 1,
                  marginBottom: 8,
                }}
              >
                Free Explorer
              </div>
              <div
                style={{
                  fontSize: 38,
                  fontWeight: 800,
                  color: "#ffffff",
                  marginBottom: 12,
                }}
              >
                $0
              </div>
              <p
                style={{
                  margin: "0 0 20px",
                  fontSize: 14,
                  color: "#aaa",
                  lineHeight: 1.5,
                }}
              >
                For occasional quote finding, students, and personal scene
                checks.
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 14,
                  lineHeight: 2,
                  color: "#ccc",
                }}
              >
                <li>10 search queries every month</li>
                <li>Full web chat app access</li>
                <li>Subtitle dialogue search</li>
                <li>Standard resolution video cuts</li>
                <li>No credit card required</li>
              </ul>
            </div>

            <a
              href={ROUTES.APP}
              style={{
                marginTop: 28,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 48,
                minHeight: 48,
                borderRadius: 10,
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                color: "#ffffff",
                fontSize: 15,
                fontWeight: 600,
                textDecoration: "none",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                boxSizing: "border-box",
              }}
            >
              Start Free
            </a>
          </div>

          {/* Pro Credits */}
          <div
            style={{
              backgroundColor: "#241824",
              border: "1px solid rgba(224, 192, 216, 0.35)",
              borderRadius: 14,
              padding: 28,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: "0 10px 30px rgba(110, 53, 91, 0.2)",
            }}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color: "#e0c0d8",
                    letterSpacing: 1,
                  }}
                >
                  Pro & API Packs
                </div>
                <span
                  style={{
                    fontSize: 11,
                    padding: "2px 8px",
                    borderRadius: 4,
                    backgroundColor: "rgba(224, 192, 216, 0.2)",
                    color: "#f5d5ee",
                    fontWeight: 700,
                  }}
                >
                  Most Flexible
                </span>
              </div>
              <div
                style={{
                  fontSize: 38,
                  fontWeight: 800,
                  color: "#ffffff",
                  marginBottom: 12,
                }}
              >
                $5{" "}
                <span
                  style={{ fontSize: 16, fontWeight: 400, color: "#bbb" }}
                >
                  / pack
                </span>
              </div>
              <p
                style={{
                  margin: "0 0 20px",
                  fontSize: 14,
                  color: "#e0c0d8",
                  lineHeight: 1.5,
                }}
              >
                For video essayists, content creators, and autonomous AI agents.
              </p>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 18,
                  fontSize: 14,
                  lineHeight: 2,
                  color: "#ddd",
                }}
              >
                <li>High-capacity video processing credits</li>
                <li>Priority video processing queue</li>
                <li>9:16 vertical crop export</li>
                <li>Lossless high definition frame extraction</li>
                <li>Full REST API token and Agent Skill access</li>
              </ul>
            </div>

            <a
              href={ROUTES.PRICING}
              style={{
                marginTop: 28,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 48,
                minHeight: 48,
                borderRadius: 10,
                backgroundColor: "#4a223e",
                color: "#f5d5ee",
                fontSize: 15,
                fontWeight: 600,
                textDecoration: "none",
                border: "1px solid rgba(224, 192, 216, 0.4)",
                boxSizing: "border-box",
              }}
            >
              View Pricing Options
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section
        style={{
          width: "100%",
          maxWidth: 960,
          padding: isMobile ? "24px 16px" : "40px 24px",
          boxSizing: "border-box",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <h2
            style={{
              margin: "0 0 12px",
              fontSize: isMobile ? 26 : 36,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Frequently Asked Questions
          </h2>
          <p style={{ margin: 0, fontSize: 16, color: "#999" }}>
            Details on search, formats, fair use, and developer integration.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div
                key={item.q}
                style={{
                  backgroundColor: "#201c23",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  borderRadius: 12,
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "18px 20px",
                    backgroundColor: "transparent",
                    border: "none",
                    textAlign: "left",
                    color: "#ffffff",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    boxSizing: "border-box",
                  }}
                >
                  <span>{item.q}</span>
                  <span
                    style={{
                      fontSize: 18,
                      color: "#e0c0d8",
                      marginLeft: 12,
                      transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  >
                    +
                  </span>
                </button>
                {isOpen && (
                  <div
                    style={{
                      padding: "16px 20px 24px",
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: "#b0b0b0",
                      borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                    }}
                  >
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Final Conversion Banner */}
      <section
        style={{
          width: "100%",
          maxWidth: 960,
          padding: isMobile ? "32px 16px 48px" : "48px 24px 72px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            backgroundColor: "#281728",
            border: "1px solid rgba(224, 192, 216, 0.3)",
            borderRadius: 16,
            padding: isMobile ? "32px 20px" : "48px",
            textAlign: "center",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.4)",
          }}
        >
          <h2
            style={{
              margin: "0 0 16px",
              fontSize: isMobile ? 26 : 40,
              fontWeight: 800,
              color: "#ffffff",
            }}
          >
            Stop scrubbing through two-hour timelines
          </h2>
          <p
            style={{
              margin: "0 auto 32px",
              maxWidth: 600,
              fontSize: isMobile ? 15 : 18,
              lineHeight: 1.6,
              color: "#d0b5cc",
            }}
          >
            Locate the exact line, extract clean MP4 clips, or grab native HD
            frames in seconds.
          </p>
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: 14,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <a
              href={ROUTES.APP}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                height: 48,
                minHeight: 48,
                width: isMobile ? "100%" : "auto",
                padding: "0 32px",
                borderRadius: 10,
                backgroundColor: "#4a223e",
                color: "#f5d5ee",
                fontSize: 16,
                fontWeight: 600,
                textDecoration: "none",
                border: "1px solid rgba(224, 192, 216, 0.45)",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              <img
                src="/favicon.svg"
                alt=""
                width={20}
                height={20}
                style={{ display: "block" }}
              />
              <span>
                {isLoggedIn ? "Open Web App" : "Launch Free in Browser"}
              </span>
            </a>
            <a
              href={ROUTES.DOCS}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: 48,
                minHeight: 48,
                width: isMobile ? "100%" : "auto",
                padding: "0 24px",
                borderRadius: 10,
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                color: "#ffffff",
                fontSize: 15,
                fontWeight: 500,
                textDecoration: "none",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                boxSizing: "border-box",
                cursor: "pointer",
              }}
            >
              Read API Documentation
            </a>
          </div>
        </div>
      </section>

      {/* Footer Navigation */}
      <footer
        style={{
          width: "100%",
          borderTop: "1px solid rgba(255, 255, 255, 0.08)",
          padding: "32px 24px 48px",
          backgroundColor: "#161318",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxWidth: 960,
            margin: "0 auto",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/favicon.svg"
              alt="find-scene"
              width={28}
              height={28}
              style={{ display: "block" }}
            />
            <span
              style={{
                fontWeight: 700,
                color: "#ffffff",
                fontSize: 16,
                letterSpacing: 0.3,
              }}
            >
              find-scene
            </span>
            <span style={{ color: "#666", fontSize: 13 }}>
              Cinema intelligence & scene extraction
            </span>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 20,
              fontSize: 13,
            }}
          >
            <a
              href={ROUTES.APP}
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              Web App
            </a>
            <a
              href={ROUTES.DOCS}
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              API Docs
            </a>
            <a
              href={ROUTES.PRICING}
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              Pricing
            </a>
            <a
              href={ROUTES.SETTINGS}
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              Settings
            </a>
            <a
              href={ROUTES.TERMS}
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              Terms
            </a>
            <a
              href={ROUTES.PRIVACY}
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              Privacy
            </a>
            <a
              href="https://discord.gg/JBbBgLy4kN"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#e0c0d8", textDecoration: "none" }}
            >
              Discord
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
